import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { Modal, ModalBody, ModalFooter, ModalHeader } from '@msdyn365-commerce-modules/utilities';
/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as React from 'react';
import { InvoiceModel } from '../helpers/invoice-data';

interface IRequestInvoiceModalProps {
    className: string;
    email: string;
    isModalOpen: boolean;
    submitRequestLabel: string;
    modalHeaderLabel: string;
    sendToEmailLabel: string;
    cancelLabel: string;
    searchPlaceholderLabel: string;
    invoices: InvoiceModel[];
    invoicesFoundString: string;
    singleInvoiceFoundString: string;
    toggle(): void;
    onSubmitRequest(invoiceId: string): void;
}

// tslint:disable-next-line:max-func-body-length
export const RequestInvoiceModal: React.FC<IRequestInvoiceModalProps> = ({ className, email, isModalOpen, toggle, modalHeaderLabel, sendToEmailLabel, submitRequestLabel, cancelLabel, onSubmitRequest, invoices, searchPlaceholderLabel,
    invoicesFoundString, singleInvoiceFoundString }) => {
    const [selectedInvoiceIndex, setSelectedInvoiceIndex] = React.useState<number>(-1);
    const [filteredInvoices, setFilteredInvoices] = React.useState<InvoiceModel[]>(invoices);
    const [searchInput, setSearchInput] = React.useState<string>('');
    const [shouldShowSearchResults, setShouldShowSearchResults] = React.useState<boolean>(false);

    const onSubmitRequestClick = () => {
        if (selectedInvoiceIndex >= 0) {
            onClosing();
            onSubmitRequest(filteredInvoices[selectedInvoiceIndex].id);
        }
    };

    /**
     * Sets the filtered invoices to the ones that were selected on the main screen.
     */
    const setFilteredInvoicesToSelectedInvoices = () => {
        const selectedInvoices = invoices.filter(invoice => invoice.isSelected);
        if (ArrayExtensions.hasElements(selectedInvoices)) {
            setFilteredInvoices(selectedInvoices);
        }
    };

    if (StringExtensions.isNullOrWhitespace(searchInput) && !ArrayExtensions.hasElements(filteredInvoices)) {
        setFilteredInvoicesToSelectedInvoices();
    }

    let invoiceOptions = renderInvoiceOptions(filteredInvoices, setSelectedInvoiceIndex, className, selectedInvoiceIndex);

    const searchInputChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchInput(e.target.value);
    };

    const searchEntered = () => {
        let updatedInvoices = invoices;
        if (!StringExtensions.isNullOrEmpty(searchInput)) {
            updatedInvoices = invoices.filter((invoice) => invoice.id.indexOf(searchInput) >= 0);
        }

        setFilteredInvoices(updatedInvoices);

        if (ArrayExtensions.hasElements(filteredInvoices)) {
            setSelectedInvoiceIndex(0);
            setShouldShowSearchResults(true);
        } else {
            setSelectedInvoiceIndex(-1);
            setShouldShowSearchResults(false);
        }

        invoiceOptions = renderInvoiceOptions(filteredInvoices, setSelectedInvoiceIndex, className, selectedInvoiceIndex);
    };

    const onClosing = () => {
        setSelectedInvoiceIndex(-1);
        setFilteredInvoices(invoices);
        setSearchInput('');
        setShouldShowSearchResults(false);
    };

    const onCancel = () => {
        onClosing();
        toggle();
    };

    /**
     * Sets the invoices that are possible to select when the dialog is opened.
     */
    // tslint:disable-next-line:typedef
    function onOpened() {
        setFilteredInvoicesToSelectedInvoices();
    }

    const searchResultString = filteredInvoices.length === 1 ? singleInvoiceFoundString : invoicesFoundString;
    const invoicesFoundReplaceIndex = searchResultString.indexOf('{0}');

    return (
        <Modal
            className='msc-modal-input-required'
            isOpen={isModalOpen}
            horizontalPosition={'center'}
            verticalPosition={'center'}
            autoFocus={true}
            fade={false}
            toggle={onCancel}
            onOpened={onOpened}
        >
            <ModalHeader toggle={onCancel}>{modalHeaderLabel}</ModalHeader>
            <ModalBody>
                <div className={`${className}__requestInvoice__modalBody`}>
                    <label htmlFor='sendToEmail'>
                        {sendToEmailLabel}
                    </label>
                    <div id='sendToEmail' className={`${className}__requestInvoice__sendToEmail`}>
                        {email}
                    </div>
                    <div>
                        {shouldShowSearchResults &&
                        <div className={`${className}__requestInvoice__searchResultDisplay`}>
                            <span>{searchResultString.substring(0, invoicesFoundReplaceIndex)}</span>
                            <span className={`${className}__requestInvoice__searchResultNumber`}>{filteredInvoices.length}</span>
                            <span>{searchResultString.substring(invoicesFoundReplaceIndex+3)}</span>
                        </div>
                    }
                    <div className={`${className}__requestInvoice__search`}>
                        <input type='text' id='searchBar' className={`${className}__requestInvoice__searchBar`} placeholder={searchPlaceholderLabel} onChange={searchInputChanged} />
                        <button className={`${className}__requestInvoice__searchButton`} onClick={searchEntered}/>
                    </div>
                    {invoiceOptions}
                </div>
            </div>
            </ModalBody>
            <ModalFooter>
                <button className={`${className}__requestInvoice__submitRequest`} onClick={onSubmitRequestClick}>{submitRequestLabel}</button>
                <button className={`${className}__requestInvoice__cancelRequest`} onClick={onCancel}>{cancelLabel}</button>
            </ModalFooter>
        </Modal>
    );
};

const renderInvoiceOptions = (filteredInvoices: InvoiceModel[], setSelectedInvoiceIndex: (newIndex: number) => void, className: string, selectedInvoiceIndex: number) => {
    return (
        <div>
            {filteredInvoices.map((invoice, index) => {
                const onInvoiceSelect = () => {
                    setSelectedInvoiceIndex(index);
                };

                const radioButtonId = `invoice__${invoice.id}`;

                return (
                    <div key={index} className={`${className}__requestInvoice__filteredInvoiceResult`}>
                        <input type='radio' id={radioButtonId} name='invoiceSelect' value={invoice.id} key={invoice.id} checked={index === selectedInvoiceIndex} aria-checked={index === selectedInvoiceIndex} onChange={onInvoiceSelect} />
                        <label htmlFor={radioButtonId} className={`${className}__requestInvoice__selectedInvoiceButton`}>{invoice.id}</label>
                    </div>
                );
            })}
        </div>
    );
};